import posthog from 'posthog-js';

let posthogInitialized = false;

const APP_NAME = 'upscore-frontend';

const decodeJwtPayload = (token) => {
  if (!token || typeof token !== 'string') return null;
  const segment = token.split('.')[1];
  if (!segment) return null;
  try {
    return JSON.parse(atob(segment));
  } catch (e) {
    return null;
  }
};

// Decode the Cognito JWTs from localStorage and identify the user with PostHog
// *before* any feature-flag evaluation runs. The backend uses the access
// token's `sub` claim as `user.user_id` (see
// common/middlewares/handleAccountAuth.js), so identifying by `sub` here
// matches what useUser later calls posthog.identify with — avoiding flag
// captures under the anonymous distinct_id during the window where getUser()
// is still in flight. We also pull `email` from the ID token so PostHog's UI
// labels the user by email from the very first captured event, instead of
// waiting for posthog.people.set in useUser.
const identifyFromJwt = (ph) => {
  try {
    if (typeof window === 'undefined') return;
    const raw = window.localStorage?.getItem('jwt-tokens');
    if (!raw) return;
    const tokens = JSON.parse(raw);

    const accessPayload = decodeJwtPayload(tokens?.accessToken);
    const sub = accessPayload?.sub;
    if (!sub || typeof sub !== 'string') return;

    const idPayload = decodeJwtPayload(tokens?.idToken);
    const email =
      idPayload?.email && typeof idPayload.email === 'string' ? idPayload.email : null;

    ph.identify(sub, email ? { email } : undefined);
  } catch (e) {
    // Malformed token or storage unavailable — fall through; useUser will
    // identify once getUser resolves.
  }
};

export const initPostHog = () => {
  if (posthogInitialized) return posthog;
  posthogInitialized = true;

  posthog.init(process.env.REACT_APP_POSTHOG_KEY, {
    api_host: process.env.REACT_APP_POSTHOG_HOST,
    ui_host: 'https://eu.posthog.com',
    defaults: '2025-11-30',
    disable_surveys: true,
    evaluation_environments: [
      APP_NAME,
      process.env.REACT_APP_ENV,
      `${APP_NAME}-${process.env.REACT_APP_ENV}`
    ],
    loaded: (ph) => {
      if (process.env.REACT_APP_ENV === 'local') {
        ph.opt_out_capturing();
      }
    }
  });

  posthog.register_once({
    app_env: `${APP_NAME}-${process.env.REACT_APP_ENV}`,
    app: APP_NAME,
    environment: process.env.REACT_APP_ENV
  });

  posthog.setPersonPropertiesForFlags({
    environment: process.env.REACT_APP_ENV
  });

  posthog.people.set({
    environment: process.env.REACT_APP_ENV
  });

  identifyFromJwt(posthog);

  return posthog;
};

export const getPostHog = () => {
  if (!posthogInitialized) {
    initPostHog();
  }
  return posthog;
};

export const isPostHogInitialized = () => posthogInitialized;

// Schedule PostHog initialization after first paint
export const schedulePostHogInit = () => {
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(() => initPostHog(), { timeout: 3000 });
  } else {
    setTimeout(() => initPostHog(), 2000);
  }
};
